import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { CircularProgress, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAsyncAction, useStateRef } from 'app/services';
import { geofenceGroupLoad, geofenceTypeLoad } from 'app/store/geofence';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const MapFilterGeofenceForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);

  const { asyncAction: fetchTypeData, isLoading: isFetchTypeLoading } = useAsyncAction(geofenceTypeLoad);
  const { asyncAction: fetchGroupData, isLoading: isFetchGroupLoading } = useAsyncAction(geofenceGroupLoad);
  const { defaultValues, onSubmit } = props;
  const { typeItems, groupItems } = useSelector((state) => state.geofence);
  const { reset, register, control, formState, handleSubmit } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      short_name: defaultValues ? (defaultValues?.short_name ?? '') : '',
      group_id: defaultValues ? defaultValues.group_id : '',
      type_id: defaultValues ? defaultValues.type_id : '',
      latitude: defaultValues ? defaultValues.latitude : '',
      longitude: defaultValues ? defaultValues.longitude : '',
      radius: defaultValues ? defaultValues.radius : '',
      location: defaultValues ? (defaultValues?.location ?? '') : '',
      ref_id: defaultValues ? (defaultValues?.ref_id ?? '') : '',
      bt_id: defaultValues ? (defaultValues?.bt_id ?? '') : '',
      keys: defaultValues ? (defaultValues?.keys ?? '') : '',
      shape: defaultValues ? (defaultValues.shape ? defaultValues.shape : 'Circle') : 'Circle',
    },
  });

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          name: data.name,
          short_name: data.short_name ? data.short_name : undefined,
          group_id: data.group_id ? data.group_id : undefined,
          type_id: data.type_id ? data.type_id : undefined,
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
          radius: parseInt(data.radius),
          location: data.location ? data.location : undefined,
          ref_id: data.ref_id ? parseInt(data.ref_id) : undefined,
          bt_id: data.bt_id ? data.bt_id : undefined,
          keys: data.keys ? data.keys : undefined,
          shape: data.shape,
        })
      )();
    },
  }));

  useEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        short_name: defaultValues?.short_name ?? '',
        group_id: defaultValues?.group_id ?? '',
        type_id: defaultValues?.type_id ?? '',
        latitude: defaultValues.latitude,
        longitude: defaultValues.longitude,
        radius: defaultValues.radius,
        location: defaultValues?.location ?? '',
        ref_id: defaultValues?.ref_id ?? '',
        bt_id: defaultValues?.bt_id ?? '',
        keys: defaultValues.keys ? defaultValues.keys.join(';') : '',
        shape: defaultValues.shape ? defaultValues.shape : 'Circle',
      });
    }
  }, [defaultValues]);

  useEffect(() => {
    fetchTypeData();
    fetchGroupData();
  }, []);

  return (
    <form>
      <FormControl error={!!formState.errors.name} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Название</InputLabel>
        <OutlinedInput
          label='Название'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Название геозоны'
          {...register('name', {
            pattern: /^([A-Za-zА-Яа-я0-9_\-\W\s]{5,30})$/iu,
            required: true,
            minLength: 5,
            maxLength: 30,
          })}></OutlinedInput>
        <FormHelperText>Используйте в названии не менее 5 и не более 30 символов</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.short_name} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Краткое название</InputLabel>
        <OutlinedInput
          label='Краткое название'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Краткое название геозоны'
          {...register('short_name', {
            pattern: /^([A-Za-zА-Яа-я0-9_\-\W\s]{0,20})$/iu,
            required: false,
            maxLength: 20,
          })}></OutlinedInput>
        <FormHelperText>Используйте в названии не более 20 символов</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.group_id} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Группа принадлежности</InputLabel>
        {isFetchGroupLoading && (
          <OutlinedInput
            disabled
            endAdornment={
              <InputAdornment position='end'>
                <CircularProgress size={16} />
              </InputAdornment>
            }
          />
        )}
        {!isFetchGroupLoading && (
          <Controller
            name='group_id'
            control={control}
            render={({ field }) => (
              <Select
                label='Группа принадлежности'
                IconComponent={KeyboardArrowDownRounded}
                MenuProps={{
                  sx: {
                    '.MuiPaper-root': {
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: '1px',
                    },
                    '.MuiPaper-root:before': {
                      display: 'none',
                    },
                  },
                }}
                {...register(field.name, {
                  pattern: /^[0-9]$/iu,
                  required: true,
                })}
                value={field.value}
                notched={firstUpdate}
                onChange={field.onChange}>
                {groupItems.map((value, key) => (
                  <MenuItem key={key} value={value.id}>
                    {value.name}
                  </MenuItem>
                ))}
              </Select>
            )}></Controller>
        )}
        <FormHelperText>Выберите группу принадлежности геозоны</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.type_id} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Тип принадлежности</InputLabel>
        {isFetchTypeLoading && (
          <OutlinedInput
            disabled
            endAdornment={
              <InputAdornment position='end'>
                <CircularProgress size={16} />
              </InputAdornment>
            }
          />
        )}
        {!isFetchTypeLoading && (
          <Controller
            name='type_id'
            control={control}
            render={({ field }) => (
              <Select
                label='Тип принадлежности'
                IconComponent={KeyboardArrowDownRounded}
                MenuProps={{
                  sx: {
                    '.MuiPaper-root': {
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: '1px',
                    },
                    '.MuiPaper-root:before': {
                      display: 'none',
                    },
                  },
                }}
                {...register(field.name, {
                  pattern: /^[0-9]$/iu,
                  required: true,
                })}
                value={field.value}
                notched={firstUpdate}
                onChange={field.onChange}>
                {typeItems.map((value, key) => (
                  <MenuItem key={key} value={value.id}>
                    {value.name}
                  </MenuItem>
                ))}
              </Select>
            )}></Controller>
        )}
        <FormHelperText>Выберите тип принадлежности геозоны</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.latitude} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Широта</InputLabel>
        <OutlinedInput
          label='Широта'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Широта геозоны'
          {...register('latitude', {
            pattern: /^(\+|-)?(?:90(?:(?:\.0{1,15})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,15})?))$/iu,
            required: true,
          })}></OutlinedInput>
        <FormHelperText>Укажите широту в формате 55.755864, используйте не более 15 символов после точки</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.longitude} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Долгота</InputLabel>
        <OutlinedInput
          label='Долгота'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Долгота геозоны'
          {...register('longitude', {
            pattern: /^(\+|-)?(?:180(?:(?:\.0{1,15})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,15})?))$/iu,
            required: true,
          })}></OutlinedInput>
        <FormHelperText>Укажите долготу в формате 37.617698, используйте не более 15 символов после точки</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.radius} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Радиус</InputLabel>
        <OutlinedInput
          label='Радиус'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Радиус геозоны'
          {...register('radius', {
            pattern: /^[0-9]+$/iu,
            required: true,
          })}></OutlinedInput>
        <FormHelperText>Укажите радиус геозоны, используйте целое положительное число</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.location} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Местоположение</InputLabel>
        <OutlinedInput
          label='Местоположение'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Местоположение геозоны'
          {...register('location', {
            pattern: /^([A-Za-zА-Яа-я0-9_\-\W\s]{0,150})$/iu,
            required: false,
            maxLength: 150,
          })}></OutlinedInput>
        <FormHelperText>Используйте в описании местоположения не более 150 символов</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.ref_id} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>RefId</InputLabel>
        <OutlinedInput
          label='RefId'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Идентификатор для ретрансляции геозоны'
          {...register('ref_id', {
            pattern: /^([0-9]{0,15})$/iu,
            required: false,
            maxLength: 15,
          })}></OutlinedInput>
        <FormHelperText>Укажите идентификатор для ретрансляции, используйте не более 15 символов</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.bt_id} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>BtId</InputLabel>
        <OutlinedInput
          label='BtId'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Bluetooth-метка геозоны'
          {...register('bt_id', {
            pattern: /^([A-F0-9]{0,8})$/iu,
            required: false,
            minLength: 8,
          })}></OutlinedInput>
        <FormHelperText>Укажите Bluetooth-метку, используйте 8 символов (буквы A-F и цифры 0-9)</FormHelperText>
      </FormControl>
      <FormControl error={!!formState.errors.keys} margin='normal' variant='outlined' fullWidth>
        <InputLabel disableAnimation={firstUpdate}>Ключи</InputLabel>
        <OutlinedInput
          label='Ключи'
          variant='outlined'
          notched={firstUpdate}
          fullWidth
          placeholder='Ключи геозоны'
          {...register('keys', {
            pattern: /^[A-Za-z0-9\s;]+$/iu,
            required: false,
          })}></OutlinedInput>
        <FormHelperText>Введите ключи снятия с охраны через точку с запятой</FormHelperText>
      </FormControl>
    </form>
  );
});

export default MapFilterGeofenceForm;
